import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { DatePicker, Form, Input, Modal, Select } from 'antd';

import { Button } from '@cropler/ui-kit';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import styles from './styles.module.scss';
import stylesModal from '../../styles/modal.module.scss';

import { notesApi, useCreateNoteMutation, useDeleteNoteMutation, useEditNoteMutation } from '../../store/api/notes';
import { Note } from './index.jsx';
import { getNoteTypes } from '../../utils/notes.jsx';
import { DateLLFormat } from '../../constants/data';

const NotesPanel = ({ t, notes, color, locationId, date, groupByDates, isDateStatic = false }) => {
  const dispatch = useDispatch();
  const [activeAddNote, setActiveAddNote] = useState(false);
  const [activeEditNote, setActiveEditNote] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const NoteTypes = getNoteTypes(t);

  const [createNote, { isSuccess: isCreateNoteSuccess }] = useCreateNoteMutation();
  const [editNote] = useEditNoteMutation();
  const [deleteNote, { isSuccess: isDeleteNoteSuccess }] = useDeleteNoteMutation();

  const [formNote] = Form.useForm();

  const openAddNoteModal = ({ date }) => {
    if (!isDateStatic) {
      formNote.setFieldValue('date', dayjs(date));
    }
    setActiveEditNote(false);
    setActiveAddNote(true);
  };

  const onOpenEditNote = ({ id, date, content, type }) => {
    if (!isDateStatic) {
      formNote.setFieldValue('date', dayjs(date));
    }
    formNote.setFieldValue('type', type);
    formNote.setFieldValue('comment', content);
    setCurrentNote(id);
    setActiveEditNote(true);
    setActiveAddNote(true);
  };

  const onCreateNote = () => {
    const note = formNote.getFieldValue('comment');
    const noteDate = isDateStatic ? date : formNote.getFieldValue('date');
    const noteType = formNote.getFieldValue('type');

    if (note && noteType) {
      createNote({
        locationId,
        date: dayjs(noteDate).format('YYYY-MM-DD'),
        content: note,
        noteType,
      });
      formNote.setFieldValue('note', '');
      setActiveAddNote(false);
    }
  };

  const onRemoveNote = ({ id }) => {
    deleteNote({ noteId: id });
  };

  const onEditNote = () => {
    const note = formNote.getFieldValue('comment');
    const noteDate = isDateStatic ? date : formNote.getFieldValue('date');
    const noteType = formNote.getFieldValue('type');

    if (note && noteType) {
      editNote({
        noteId: currentNote,
        locationId,
        date: dayjs(noteDate).format('YYYY-MM-DD'),
        content: note,
        noteType,
      });
      formNote.setFieldValue('note', '');
    }

    let newNotes = [...notes];

    let indexNote = 0;
    newNotes.map((item, index) => {
      if (item.id === currentNote) {
        indexNote = index;
      }
    });
    newNotes[indexNote] = {
      ...newNotes[0],
      content: note,
    };

    setActiveEditNote(false);
    setActiveAddNote(false);
  };

  useEffect(() => {
    if (isCreateNoteSuccess || isDeleteNoteSuccess) {
      dispatch(notesApi.util.resetApiState());
    }
  }, [isCreateNoteSuccess, isDeleteNoteSuccess]);

  const renderNote = (obj) => (
    <Note
      key={obj.id}
      note={{
        id: obj.id,
        author: {
          id: obj.author.id,
          first_name: obj.author.first_name,
          last_name: obj.author.last_name,
          avatar: obj.author.avatar,
        },
        date: obj.date,
        created_at: obj.created_at,
        content: obj.content,
        note_type: obj.note_type,
      }}
      color={color}
      onRemoveNote={() => onRemoveNote({ id: obj.id })}
      onEditNote={() => onOpenEditNote({ id: obj.id, date: obj.date, content: obj.content, type: obj.note_type })}
      t={t}
    />
  );

  const groupNotesByDate = (items) => {
    const groups = [];
    const groupByDate = new Map();

    items.forEach((obj) => {
      if (!groupByDate.has(obj.date)) {
        const group = { date: obj.date, items: [] };
        groupByDate.set(obj.date, group);
        groups.push(group);
      }
      groupByDate.get(obj.date).items.push(obj);
    });

    return groups;
  };

  return (
    <>
      <div className={styles.notesPanelList}>
        {notes &&
          (!groupByDates
            ? notes.map((obj) => renderNote(obj))
            : groupNotesByDate(notes).map((group) => (
                <Fragment key={group.date}>
                  <div className={styles.notesPanelGroupTitle}>{dayjs(group.date).format(DateLLFormat)}</div>
                  {group.items.map((obj) => renderNote(obj))}
                </Fragment>
              )))}
      </div>
      <Button
        className={styles.notesPanelNav}
        icon={<CroplerIcon name="add" />}
        type="primary"
        size={'large'}
        onClick={() => openAddNoteModal({ date: date })}
      >
        {t('notes.buttonAdd')}
      </Button>

      <Modal
        title={activeEditNote ? t('notes.edit') : t('notes.add')}
        open={activeAddNote}
        onCancel={() => setActiveAddNote(false)}
        footer={false}
        centered
        width={450}
      >
        <Form form={formNote} layout="vertical" requiredMark={false} key={currentNote.id}>
          {isDateStatic ? (
            <div className={styles.noteModalTitle}>{dayjs(date).format(DateLLFormat)}</div>
          ) : (
            <Form.Item
              label={t('notes.dateOfNote')}
              name="date"
              rules={[
                {
                  required: true,
                  message: t('notes.enterDate'),
                },
              ]}
            >
              <DatePicker size={'large'} />
            </Form.Item>
          )}
          <Form.Item label={t('notes.workType')} name="type">
            <Select suffixIcon={<CroplerIcon name="down" />} size={'large'}>
              <Select.Option value={NoteTypes.harvesting.id}>{NoteTypes.harvesting.title}</Select.Option>
              <Select.Option value={NoteTypes.planting.id}>{NoteTypes.planting.title}</Select.Option>
              <Select.Option value={NoteTypes.text.id}>{NoteTypes.text.title}</Select.Option>
              <Select.Option value={NoteTypes.other.id}>{NoteTypes.other.title}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={t('notes.comment')}
            name="comment"
            rules={[
              {
                required: true,
                type: 'string',
                max: 512,
                min: 1,
                message: t('notes.commentPlaceholder'),
              },
            ]}
          >
            <Input.TextArea maxLength={512} placeholder={t('notes.commentPlaceholder')} rows={5} />
          </Form.Item>
          <div className={stylesModal.modalNav}>
            <Button type="secondary" onClick={() => setActiveAddNote(false)}>
              {t('actions.cancel')}
            </Button>
            {activeEditNote ? (
              <Button type="primary" icon={<CroplerIcon name="check" />} htmlType="submit" onClick={() => onEditNote()}>
                {t('notes.buttonSave')}
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<CroplerIcon name="check" />}
                htmlType="submit"
                onClick={() => onCreateNote()}
              >
                {t('notes.buttonAdd')}
              </Button>
            )}
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default NotesPanel;
